/**
 * MonarchPopup — standalone popup for the Monarch Assurance Investment card.
 *
 * Used by users who hold the joint Monarch MIP via a 'Monarch Assurance'
 * Investment doc (currently Carl). Fetches the joint position detail from
 * /api/monarch/state and renders the same per-product breakdown Kristof
 * sees inside Brutus EntityPopup, but with "Your share" labelling instead
 * of "Brutus 50%".
 *
 * Created 21 Jun 2026 — Phase 4 multi-user.
 */
import React, { useState, useEffect } from 'react';
import { apiGet } from '../../utils/api';
import SeeFilesButton from './SeeFilesButton';
import { getDriveFolderUrl } from './driveFolders';

// ─── Tiny shared formatters (kept local — no shared lib needed) ──────────────
const fmtEur = (v, dec = 0) => {
  if (v == null || isNaN(v)) return '—';
  return '€' + Number(v).toLocaleString(undefined, { maximumFractionDigits: dec, minimumFractionDigits: dec });
};
const fmtDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtPct = (v, dec = 1) => {
  if (v == null) return '—';
  const s = v >= 0 ? '+' : '';
  return `${s}${(v * 100).toFixed(dec)}%`;
};
const pctColor = (v) => v == null ? '#6b7280' : (v >= 0 ? '#16a34a' : '#dc2626');

// Plain-language payoff description, derived dynamically from product data.
// Two variants: autocall_degressive (annual observation, descending threshold)
// and two_index_participation (10Y, upside + DIP protection barrier).
function describeProduct(prod, autocallIdx, upIdx, dipIdx) {
  if (prod.payoff_type === 'autocall_degressive' && prod.autocall_schedule && autocallIdx) {
    const obs = prod.autocall_schedule.observations || [];
    const first = obs[0];
    const last  = obs[obs.length - 1];
    const final = prod.autocall_schedule.final_maturity;
    const winThr = (final?.scenarios?.win_threshold_pct ?? 1) * 100;
    const winPay = (final?.scenarios?.win_payout_pct ?? 1) * 100;
    const protLow = (final?.scenarios?.protected_low_pct ?? 0.7) * 100;
    const protPay = (final?.scenarios?.protected_payout_pct ?? 1) * 100;
    const lossThr = (final?.scenarios?.loss_threshold_pct ?? 0.7) * 100;

    return (
      <>
        <p style={{ margin: '0 0 8px 0' }}>
          <b>Autocallable structured note.</b> Each year on the observation date,
          if <i>{autocallIdx.name || autocallIdx.ticker}</i> closes at or above the year's
          threshold (starting <b>{first ? (first.threshold_pct * 100).toFixed(0) : '—'}%</b> of strike,
          stepping down to <b>{last ? (last.threshold_pct * 100).toFixed(0) : '—'}%</b> by year {obs.length}),
          the product <b>autocalls early</b> and pays{' '}
          <b>{first ? (first.payout_pct * 100).toFixed(0) : '—'}% → {last ? (last.payout_pct * 100).toFixed(0) : '—'}%</b>{' '}
          of capital ({first ? `+${((first.payout_pct - 1) * 100).toFixed(0)}` : '—'}%
          {last ? ` → +${((last.payout_pct - 1) * 100).toFixed(0)}%` : ''} coupon).
        </p>
        <p style={{ margin: 0 }}>
          If it never autocalls, at final maturity <b>{fmtDate(final?.date)}</b>:
          {' '}index ≥ <b>{winThr.toFixed(0)}%</b> of strike → pays <b>{winPay.toFixed(0)}%</b> of capital;
          {' '}between <b>{protLow.toFixed(0)}%</b> and <b>{winThr.toFixed(0)}%</b> → <b>{protPay.toFixed(0)}%</b> capital back;
          {' '}below <b>{lossThr.toFixed(0)}%</b> → you take the index loss 1-for-1.
        </p>
      </>
    );
  }

  if (prod.payoff_type === 'two_index_participation' && upIdx && dipIdx) {
    const participation = prod.payoff_params?.participation ?? 1;
    const dipBarrier = prod.payoff_params?.dip_barrier_pct ?? 0.5;
    const cap = prod.payoff_params?.cap_pct;
    return (
      <>
        <p style={{ margin: '0 0 8px 0' }}>
          <b>10-year participation note with capital protection.</b> Two underlyings drive the payoff:
          <i> {upIdx.name || upIdx.ticker}</i> ({upIdx.role}) provides the upside;
          <i> {dipIdx.name || dipIdx.ticker}</i> ({dipIdx.role}) acts as the protection trigger.
        </p>
        <p style={{ margin: 0 }}>
          At maturity <b>{fmtDate(prod.maturity_date)}</b>: if <i>{dipIdx.ticker}</i> has <b>not</b> fallen more than{' '}
          <b>{(dipBarrier * 100).toFixed(0)}%</b> below its strike, you receive{' '}
          <b>100% of capital + {(participation * 100).toFixed(0)}% × max(0, upside performance)</b>
          {cap ? `, capped at ${(cap * 100).toFixed(1)}% extra` : ' (no cap on upside)'}.
          {' '}If <i>{dipIdx.ticker}</i> closes <b>below the −{(dipBarrier * 100).toFixed(0)}% barrier</b>,
          you take the DIP loss 1-for-1 — capital protection is lost.
        </p>
      </>
    );
  }

  return <p style={{ margin: 0, color: '#9ca3af', fontStyle: 'italic' }}>No payoff description available for type '{prod.payoff_type}'.</p>;
}


function MonarchPopup({ onClose }) {
  const [data, setData]       = useState(null);
  const [investment, setInv]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [openProductId, setOpen] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiGet('/api/monarch/state')
      .then(r => {
        if (cancelled) return;
        if (r?.success) { setData(r.data); setInv(r.investment); }
        else setError(r?.message || 'Failed to load Monarch data');
      })
      .catch(e => { if (!cancelled) setError(e.message || 'Network error'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const renderProductTile = (prod, share) => {
    const isOpen = openProductId === prod.id;
    const upIdx  = (prod.indices || []).find(i => i.role === 'upside');
    const dipIdx = (prod.indices || []).find(i => i.role === 'dip' || i.role === 'protection');
    const isAutocall = prod.payoff_type === 'autocall_degressive';
    const autocallIdx = isAutocall ? ((prod.indices || []).find(i => i.role === 'autocall_trigger') || prod.indices?.[0]) : null;

    const fvJoint = prod.fair_value?.fv_joint_eur;
    const fvShare = prod.fair_value?.fv_share_eur;
    const afkoop  = prod.monarch_afkoopwaarde_joint_eur;
    const cost    = prod.cost_paid_joint_eur;

    let headline = null;
    if (isAutocall && autocallIdx) {
      const spotVsStrike = (autocallIdx.current_spot / autocallIdx.strike) - 1;
      const nextObs = prod.autocall_schedule?.observations?.[0];
      const nextThreshold = autocallIdx.strike * (nextObs?.threshold_pct ?? 1);
      const buffer = (autocallIdx.current_spot - nextThreshold) / nextThreshold;
      const yearProb = prod.last_pricing_details?.per_year?.[0]?.probability;
      headline = (
        <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>
          Spot <b>{autocallIdx.current_spot?.toFixed(2)}</b> vs strike {autocallIdx.strike}{' '}
          <span style={{ color: pctColor(spotVsStrike) }}>({fmtPct(spotVsStrike)})</span>
          {nextObs && <> · Next obs <b>{fmtDate(nextObs.date)}</b> @ {(nextObs.threshold_pct * 100).toFixed(0)}%
            (= {nextThreshold.toFixed(2)}, buffer <span style={{ color: pctColor(buffer) }}>{fmtPct(buffer)}</span>)</>}
          {yearProb != null && <> · <b style={{ color: '#16a34a' }}>{(yearProb * 100).toFixed(1)}% autocall prob</b></>}
        </div>
      );
    } else if (upIdx && dipIdx) {
      const upPerf  = (upIdx.current_spot / upIdx.strike) - 1;
      const dipPerf = (dipIdx.current_spot / dipIdx.strike) - 1;
      const dipBarrier = prod.payoff_params?.dip_barrier_pct ?? 0.5;
      const dipBuf = (dipPerf - (-dipBarrier));
      const pWithCoupon = prod.last_pricing_details?.probability_partial_coupon != null
        ? (prod.last_pricing_details.probability_partial_coupon + (prod.last_pricing_details.probability_capped || 0))
        : null;
      headline = (
        <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>
          Upside {upIdx.ticker} <span style={{ color: pctColor(upPerf) }}>{fmtPct(upPerf)}</span>
          {' · '}DIP {dipIdx.ticker} <span style={{ color: pctColor(dipPerf) }}>{fmtPct(dipPerf)}</span>
          {' '}(buffer <span style={{ color: pctColor(dipBuf) }}>{fmtPct(dipBuf, 0)}</span> above {(dipBarrier * 100).toFixed(0)}% trigger)
          {pWithCoupon != null && <> · <b>{(pWithCoupon * 100).toFixed(0)}% coupon prob</b></>}
        </div>
      );
    }

    return (
      <div key={prod.id} style={{ border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 10, background: isOpen ? '#FAF8F6' : '#FFFFFF' }}>
        <div
          onClick={() => setOpen(isOpen ? null : prod.id)}
          style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                background: isAutocall ? '#16a34a' : '#3b82f6', color: 'white',
              }}>
                {isAutocall ? 'AUTOCALLABLE' : '10Y MATURITY'}
              </span>
              <strong>{prod.label}</strong>
              <span style={{ fontSize: 11, color: '#6b7280' }}>· {prod.isin}</span>
            </div>
            {headline}
          </div>
          <div style={{ textAlign: 'right', minWidth: 160 }}>
            <div style={{ fontSize: 11, color: '#6b7280' }}>Fair value (your 50%)</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#0f2840' }}>{fvShare ? fmtEur(fvShare, 0) : '—'}</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>{isOpen ? '▾ collapse' : '▸ expand'}</div>
          </div>
        </div>

        {isOpen && (
          <div style={{ padding: '0 14px 14px 14px', borderTop: '1px solid #e5e7eb' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12 }}>
              <div>
                <h4 style={{ margin: '0 0 6px 0', fontSize: 12, color: '#1F4E79', textTransform: 'uppercase' }}>Structure</h4>
                <table className="ent-detail-table" style={{ fontSize: 12 }}>
                  <tbody>
                    <tr><th>ISIN</th><td>{prod.isin}</td></tr>
                    <tr><th>Issuer</th><td>{prod.issuer}</td></tr>
                    <tr><th>Guarantor</th><td>{prod.guarantor}</td></tr>
                    <tr><th>Currency</th><td>{prod.currency}</td></tr>
                    <tr><th>Issue date</th><td>{fmtDate(prod.issue_date)}</td></tr>
                    <tr><th>Maturity</th><td>{fmtDate(prod.maturity_date)}</td></tr>
                    {prod.payoff_params?.participation != null && (
                      <tr><th>Participation</th><td>{(prod.payoff_params.participation * 100).toFixed(0)}%</td></tr>
                    )}
                    {prod.payoff_params?.dip_barrier_pct != null && (
                      <tr><th>DIP barrier</th><td>−{(prod.payoff_params.dip_barrier_pct * 100).toFixed(0)}% from strike</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div>
                <h4 style={{ margin: '0 0 6px 0', fontSize: 12, color: '#1F4E79', textTransform: 'uppercase' }}>Indices</h4>
                <table className="ent-detail-table" style={{ fontSize: 12 }}>
                  <thead><tr><th>Role</th><th>Ticker</th><th className="num">Spot</th><th className="num">Strike</th><th className="num">Δ%</th></tr></thead>
                  <tbody>
                    {(prod.indices || []).map((idx, i) => {
                      const perf = idx.strike ? (idx.current_spot / idx.strike) - 1 : null;
                      return (
                        <tr key={i}>
                          <td>{idx.role}</td>
                          <td>{idx.ticker}</td>
                          <td className="num">{idx.current_spot?.toFixed(2) ?? '—'}</td>
                          <td className="num">{idx.strike?.toFixed(2) ?? '—'}</td>
                          <td className="num" style={{ color: pctColor(perf) }}>{fmtPct(perf, 1)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Autocall schedule (for autocall_degressive products) */}
            {isAutocall && prod.autocall_schedule && (
              <>
                <h4 style={{ margin: '14px 0 6px 0', fontSize: 12, color: '#1F4E79', textTransform: 'uppercase' }}>Autocall schedule</h4>
                <table className="ent-detail-table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr><th>Date</th><th className="num">Threshold %</th><th className="num">Index level</th><th className="num">Payout</th><th className="num">Probability</th></tr>
                  </thead>
                  <tbody>
                    {(prod.autocall_schedule.observations || []).map((o, i) => {
                      const thr  = autocallIdx?.strike * o.threshold_pct;
                      const prob = prod.last_pricing_details?.per_year?.[i]?.probability;
                      const isNext = i === 0;
                      return (
                        <tr key={i} style={isNext ? { background: '#E8F5E9', fontWeight: 600 } : {}}>
                          <td>{fmtDate(o.date)}{isNext && ' ◄ next'}</td>
                          <td className="num">{(o.threshold_pct * 100).toFixed(0)}%</td>
                          <td className="num">{thr != null ? thr.toFixed(2) : '—'}</td>
                          <td className="num">{(o.payout_pct * 100).toFixed(0)}%</td>
                          <td className="num" style={{ color: pctColor((prob ?? 0) - 0.001) }}>{prob != null ? `${(prob * 100).toFixed(1)}%` : '—'}</td>
                        </tr>
                      );
                    })}
                    {prod.autocall_schedule.final_maturity && (
                      <tr>
                        <td>{fmtDate(prod.autocall_schedule.final_maturity.date)}</td>
                        <td className="num">final</td>
                        <td className="num">—</td>
                        <td className="num">scenarios</td>
                        <td className="num">{prod.last_pricing_details?.final_probability != null ? `${(prod.last_pricing_details.final_probability * 100).toFixed(1)}%` : '—'}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {prod.last_pricing_details?.cumulative_autocall_prob != null && (
                  <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                    Cumulative autocall probability: <b>{(prod.last_pricing_details.cumulative_autocall_prob * 100).toFixed(1)}%</b>
                  </p>
                )}
              </>
            )}

            {/* Payoff scenarios (only for two_index_participation) */}
            {!isAutocall && prod.payoff_params && upIdx && dipIdx && (
              <>
                <h4 style={{ margin: '14px 0 6px 0', fontSize: 12, color: '#1F4E79', textTransform: 'uppercase' }}>Payoff scenarios at {fmtDate(prod.maturity_date)}</h4>
                <table className="ent-detail-table" style={{ fontSize: 12 }}>
                  <thead><tr><th>Scenario</th><th>Trigger</th><th>Payout</th><th className="num">Probability</th></tr></thead>
                  <tbody>
                    <tr>
                      <td>Capital loss</td>
                      <td>DIP {dipIdx.ticker} &lt; −{(prod.payoff_params.dip_barrier_pct * 100).toFixed(0)}%</td>
                      <td>= DIP performance</td>
                      <td className="num" style={{ color: pctColor(-prod.last_pricing_details?.probability_capital_loss) }}>
                        {prod.last_pricing_details?.probability_capital_loss != null ? `${(prod.last_pricing_details.probability_capital_loss * 100).toFixed(1)}%` : '—'}
                      </td>
                    </tr>
                    <tr>
                      <td>No coupon</td>
                      <td>Upside {upIdx.ticker} ≤ 0%, DIP OK</td>
                      <td>100% (capital back)</td>
                      <td className="num">{prod.last_pricing_details?.probability_protected_no_coupon != null ? `${(prod.last_pricing_details.probability_protected_no_coupon * 100).toFixed(1)}%` : '—'}</td>
                    </tr>
                    <tr style={{ background: '#E8F5E9' }}>
                      <td><b>With coupon</b></td>
                      <td>Upside &gt; 0%, DIP OK</td>
                      <td>100% + {(prod.payoff_params.participation * 100).toFixed(0)}% × max(0, perf){prod.payoff_params.cap_pct ? `, cap ${(prod.payoff_params.cap_pct * 100).toFixed(1)}%` : ''}</td>
                      <td className="num" style={{ color: '#16a34a' }}>
                        <b>{(((prod.last_pricing_details?.probability_partial_coupon || 0) + (prod.last_pricing_details?.probability_capped || 0)) * 100).toFixed(1)}%</b>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}

            {/* Valuation */}
            <h4 style={{ margin: '14px 0 6px 0', fontSize: 12, color: '#1F4E79', textTransform: 'uppercase' }}>Valuation</h4>
            <table className="ent-detail-table" style={{ fontSize: 12 }}>
              <thead><tr><th></th><th className="num">Joint</th><th className="num">Your 50%</th></tr></thead>
              <tbody>
                <tr><th>Cost basis</th><td className="num">{fmtEur(cost)}</td><td className="num">{fmtEur(cost * share)}</td></tr>
                <tr><th>Monarch afkoopwaarde</th><td className="num">{fmtEur(afkoop)}</td><td className="num">{fmtEur(afkoop * share)}</td></tr>
                <tr style={{ background: '#FAF8F6' }}><th>MC fair value</th>
                  <td className="num"><b>{fmtEur(fvJoint, 0)}</b></td>
                  <td className="num"><b>{fmtEur(fvShare, 0)}</b></td>
                </tr>
                {fvJoint != null && cost && (
                  <tr><th>P&L vs cost (FV)</th>
                    <td className="num" style={{ color: pctColor(fvJoint - cost) }}>{fmtEur(fvJoint - cost, 0)} ({fmtPct((fvJoint - cost) / cost)})</td>
                    <td className="num" style={{ color: pctColor(fvJoint - cost) }}>{fmtEur((fvJoint - cost) * share, 0)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* How this pays out (plain-language summary) */}
            <h4 style={{ margin: '14px 0 6px 0', fontSize: 12, color: '#1F4E79', textTransform: 'uppercase' }}>How this pays out</h4>
            <div style={{ fontSize: 12, color: '#374151', background: '#FAF8F6', padding: '10px 12px', borderRadius: 6, lineHeight: 1.55 }}>
              {describeProduct(prod, autocallIdx, upIdx, dipIdx)}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBody = () => {
    if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading…</div>;
    if (error)   return <div style={{ padding: 20, color: '#dc2626' }}>{error}</div>;
    if (!data)   return null;

    const share = (data.share_pct ?? 50) / 100;
    const products = data.structured_products || [];

    const totalAfkoopJoint = products.reduce((s, x) => s + (x.monarch_afkoopwaarde_joint_eur || 0), 0)
                          || data.joint_total_afkoopwaarde_eur || 0;
    const totalFvJoint   = products.reduce((s, x) => s + (x.fair_value?.fv_joint_eur || 0), 0);
    const totalCostJoint = products.reduce((s, x) => s + (x.cost_paid_joint_eur || 0), 0);

    return (
      <>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
          Tak 23 wrapper · joint with {data.joint_with || 'Brutus & Co BV'} · your share {(share * 100).toFixed(0)}%
          {data.last_pricing_run?.as_of && <> · last pricer run {fmtDate(data.last_pricing_run.as_of)}</>}
        </div>

        {/* Top summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
          {[
            { label: 'Afkoopwaarde (your share)', value: totalAfkoopJoint * share, sub: 'Monarch surrender × 50%' },
            { label: 'Fair value (your share, MC)', value: totalFvJoint ? totalFvJoint * share : null, sub: 'Our MC pricer', highlight: true },
            { label: 'Cost basis (your share)',    value: totalCostJoint * share, sub: 'paid at inception' },
          ].map((s, i) => (
            <div key={i} style={{
              padding: 12, borderRadius: 6,
              background: s.highlight ? '#0F2840' : '#F3F4F6',
              color:      s.highlight ? 'white' : '#111827',
            }}>
              <div style={{ fontSize: 10, opacity: 0.7, textTransform: 'uppercase' }}>{s.label}</div>
              <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{s.value ? fmtEur(s.value, 0) : '—'}</div>
              <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Per-product tiles */}
        {products.length === 0 && (
          <p style={{ color: '#6b7280', fontSize: 13 }}>No product data available yet. The Monarch pricer runs nightly at 19:10 London time.</p>
        )}
        {products.map(p => renderProductTile(p, share))}

        {/* Investment metadata footnote */}
        {investment && (
          <div style={{ marginTop: 14, padding: '10px 12px', background: '#F9FAFB', borderRadius: 6, fontSize: 11, color: '#6b7280' }}>
            Dashboard value sync: €{Math.round(investment.eurValue || 0).toLocaleString()} as of {fmtDate(investment.timeOfUpdate)} · {(investment.valueHistory || []).length} history points
          </div>
        )}
      </>
    );
  };

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 980, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '16px 24px 12px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 18, color: '#0F2840' }}>
            Monarch Assurance{data?.mip_no && <> — MIP {data.mip_no}</>}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <SeeFilesButton url={getDriveFolderUrl('Monarch Assurance')} />
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6b7280' }}>×</button>
          </div>
        </div>
        <div style={{ padding: 20 }}>
          {renderBody()}
        </div>
      </div>
    </div>
  );
}

export default MonarchPopup;
